import { ThemeEvents } from '@theme/events';
import { localQuantity } from '@theme/local-baseline';

const COUNTRIES = ['US', 'DE', 'JP'];

class ThorensLocalStock extends HTMLElement {
  /** @type {number} */
  #request = 0;

  connectedCallback() {
    const section = this.closest('.shopify-section, dialog');
    section?.addEventListener('change', this.#onOptionChange);
    section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariant);
    this.#apply(this.dataset.variantId);
  }

  disconnectedCallback() {
    const section = this.closest('.shopify-section, dialog');
    section?.removeEventListener('change', this.#onOptionChange);
    section?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariant);
  }

  /** Hide immediately when the selected option belongs to another variant or product. */
  #onOptionChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const picker = target.closest('variant-picker');
    if (!picker) return;
    if (picker.dataset.productId && picker.dataset.productId !== this.dataset.productId) return;

    const option = target instanceof HTMLSelectElement ? target.options[target.selectedIndex] : target;
    const nextUrl = option?.dataset?.connectedProductUrl || '';
    const currentUrl = (this.dataset.productUrl || '').split('?')[0];
    if (nextUrl && currentUrl && nextUrl !== currentUrl) {
      this.#hide();
      return;
    }

    const variantId = option?.dataset?.variantId || '';
    if (!variantId) {
      this.#hide();
      return;
    }
    this.dataset.variantId = variantId;
    this.#apply(variantId);
  };

  /** @param {Event} event */
  #onVariant = (event) => {
    try {
      const detail = /** @type {CustomEvent} */ (event).detail;
      if (detail?.data?.newProduct) {
        this.dataset.productId = detail.data.newProduct.id;
        if (detail.data.newProduct.url) this.dataset.productUrl = detail.data.newProduct.url;
      } else if (
        event.target instanceof HTMLElement &&
        event.target.dataset.productId &&
        event.target.dataset.productId !== this.dataset.productId
      ) {
        return;
      }

      const nextScript = detail?.data?.html?.querySelector?.('thorens-local-stock [data-local-stock]');
      const currentScript = this.querySelector('[data-local-stock]');
      if (nextScript && currentScript) currentScript.textContent = nextScript.textContent;

      const variantId = detail?.resource?.id || this.dataset.variantId || '';
      if (variantId) this.dataset.variantId = String(variantId);
      this.#apply(this.dataset.variantId);
    } catch {
      this.#hide();
    }
  };

  #read() {
    const node = this.querySelector('[data-local-stock]');
    if (!node?.textContent) return null;
    return JSON.parse(node.textContent);
  }

  /**
   * @param {string | undefined} variantId
   */
  async #apply(variantId) {
    const request = ++this.#request;
    let data = null;
    try {
      data = this.#read();
    } catch {
      this.#showInternational();
      return;
    }
    if (!data) {
      this.#showInternational();
      return;
    }

    const country = String(data.country || '').toUpperCase();
    const minimum = Number(data.minimum);
    const ready = String(data.ready || '').trim();
    const place = String(data.places?.[country] || '').trim();
    const id = String(variantId || '');
    this.#showInternational();
    if (!COUNTRIES.includes(country) || !ready || !place || !Number.isFinite(minimum) || minimum < 1) return;

    try {
      const productUrl = this.dataset.productUrl || window.location.pathname;
      const qty = await localQuantity(productUrl, id, data.variants?.[id]);
      if (request !== this.#request || !this.isConnected) return;
      if (!Number.isFinite(qty) || qty < minimum) {
        this.#showInternational();
        return;
      }
      this.#show(ready, place, data.localDays);
    } catch {
      if (request !== this.#request || !this.isConnected) return;
      this.#showInternational();
    }
  }

  /**
   * @param {string} title
   * @param {string} place
   * @param {string} [localDays]
   */
  #show(title, place, localDays) {
    const titleEl = this.querySelector('.thorens-local-stock__title');
    const nameEl = this.querySelector('.thorens-local-stock__place-name');
    const daysEl = this.querySelector('.thorens-local-stock__place-days');
    const originEl = this.querySelector('.thorens-local-stock__origin');
    if (!(titleEl instanceof HTMLElement)) {
      this.#hide();
      return;
    }

    const card = this.querySelector('.thorens-local-stock__card');
    const intl = this.querySelector('.thorens-local-stock__intl');
    if (card instanceof HTMLElement) card.hidden = false;
    if (intl instanceof HTMLElement) intl.hidden = true;

    titleEl.textContent = title;
    if (nameEl) nameEl.textContent = place;
    if (daysEl) daysEl.textContent = localDays || '';
    if (originEl) originEl.hidden = place === '';
    this.hidden = false;
    if (localDays) this.#setDays(localDays);
  }

  #showInternational() {
    const card = this.querySelector('.thorens-local-stock__card');
    const intl = this.querySelector('.thorens-local-stock__intl');
    if (!(intl instanceof HTMLElement)) {
      this.#hide();
      return;
    }
    if (card instanceof HTMLElement) card.hidden = true;
    intl.hidden = false;
    this.hidden = false;
    const time = intl.querySelector('.thorens-local-stock__intl-time')?.textContent?.trim();
    this.#setDays(time || '');
  }

  /** The line under In stock uses the same window as the note below. */
  #setDays(text) {
    const days = this.closest('.shopify-section, dialog')?.querySelector('.thorens-delivery-days');
    if (days instanceof HTMLElement && text) days.textContent = text;
  }

  #hide() {
    const titleEl = this.querySelector('.thorens-local-stock__title');
    const nameEl = this.querySelector('.thorens-local-stock__place-name');
    const daysEl = this.querySelector('.thorens-local-stock__place-days');
    const card = this.querySelector('.thorens-local-stock__card');
    const intl = this.querySelector('.thorens-local-stock__intl');
    if (titleEl) titleEl.textContent = '';
    if (nameEl) nameEl.textContent = '';
    if (daysEl) daysEl.textContent = '';
    if (card instanceof HTMLElement) card.hidden = true;
    if (intl instanceof HTMLElement) intl.hidden = true;
    this.hidden = true;
  }
}

if (!customElements.get('thorens-local-stock')) {
  customElements.define('thorens-local-stock', ThorensLocalStock);
}
